import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { ChangeEvent, useState, useEffect, useCallback } from 'react';
import { Upload, HardDrive, Cloud, FileBox, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropZone } from '@/components/ui/drop-zone';
import ErrorMessage from '@/components/ErrorMessage';
import {
  IAttachmentCategory,
  IControllerType,
  IToolSetter,
  IProbeForZHeightCommand,
} from '@/redux/post/post.d';
import {
  addFileToUploadQueue,
  removeFileFromUploadQueue,
  selectProjectEditorData,
  setProjectEditorData,
  toggleController,
  selectFilesForController,
  selectControllersFromExistingModels,
} from '@/redux/post/post.slice';
import hashFile from '@/lib/xxhash';
import { parseGcodeInWorker } from '@/workers/useGcodeParser';
import {
  AttachmentParentType,
  AttachmentType,
} from '@/redux/attachment/attachment.d';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';
import {
  CONTROLLER_CONFIG,
  CONTROLLER_ORDER,
  validateFileForController,
} from '@/lib/controllerConfig';

function ModelUpload({ errors }: { errors: { [key: string]: string } }) {
  const dispatch = useDispatch();
  const projectEditorData = useSelector(selectProjectEditorData);
  const controllersFromModels = useSelector(
    selectControllersFromExistingModels
  );
  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [isDeleting, setIsDeleting] = useState('');
  const [activeTab, setActiveTab] = useState<string>('');

  const generateControllerFileName = (
    originalFileName: string,
    controller: IControllerType
  ): string => {
    const fileExtension = originalFileName.split('.').pop();
    let fileNameWithoutExtension = originalFileName.replace(/\.[^/.]+$/, '');

    // Check if filename already has a controller prefix and remove it
    // Get all possible controller names (enum values and display names)
    const controllerNames = [
      ...Object.values(IControllerType),
      ...Object.values(CONTROLLER_CONFIG).map((config) => config.name),
    ];

    // Create pattern to match any controller prefix (case insensitive)
    const controllerPattern = new RegExp(
      `^(${controllerNames.join('|')})_`,
      'i'
    );

    if (controllerPattern.test(fileNameWithoutExtension)) {
      // Remove existing controller prefix
      fileNameWithoutExtension = fileNameWithoutExtension.replace(
        controllerPattern,
        ''
      );
    }

    return `${controller}_${fileNameWithoutExtension}.${fileExtension}`;
  };

  const getSelectedControllers = useCallback(() => {
    return projectEditorData.selectedControllers;
  }, [projectEditorData.selectedControllers]);

  const getSelectedControllersInOrder = useCallback(() => {
    return CONTROLLER_ORDER.filter((controller) =>
      getSelectedControllers().includes(controller)
    );
  }, [getSelectedControllers]);

  // fallow-ignore-next-line complexity
  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
    controller: IControllerType
  ) => {
    const modelFiles = event.target.files;
    if (!modelFiles || modelFiles.length === 0) return;

    const files = Array.from(modelFiles).filter((file) =>
      validateFileForController(file.name, controller)
    );

    // Hash in parallel, but queue in the order the user picked the files.
    // Dispatching inside the Promise.all callbacks would queue them in
    // hash-completion order (big files finish last), and that order is what
    // ends up in the DB and on the "Assign Tools" step.

    const fileHashes = await Promise.all(files.map((file) => hashFile(file)));

    // Parse the first G-code file for metadata extraction (via web worker).
    // Extracted data pre-fills job setup fields so the user doesn't have to
    // type them manually.
    const gcodeFile = files[0];
    if (gcodeFile) {
      try {
        const content = await gcodeFile.text();
        const extracted = await parseGcodeInWorker(content);

        const updates: Record<string, unknown> = {};

        if (extracted.units) updates.units = extracted.units;
        if (extracted.jobType && !projectEditorData.jobType) {
          updates.jobType = extracted.jobType;
        }
        if (extracted.zZeroPosition && !projectEditorData.zZeroPosition) {
          updates.zZeroPosition = extracted.zZeroPosition;
        }
        if (extracted.dimensions) {
          const { width, height, thickness } = extracted.dimensions;
          if (width > 0 && !projectEditorData.jobSizeWidth)
            updates.jobSizeWidth = width;
          if (height > 0 && !projectEditorData.jobSizeHeight)
            updates.jobSizeHeight = height;
          if (thickness > 0 && !projectEditorData.jobSizeThickness)
            updates.jobSizeThickness = thickness;
        }
        if (extracted.hasProbing && !projectEditorData.toolSetter) {
          updates.toolSetter = IToolSetter.ENABLED;
        }
        if (extracted.hasATC && !projectEditorData.probeForZHeightCommand) {
          updates.probeForZHeightCommand = IProbeForZHeightCommand.YES;
        }

        if (Object.keys(updates).length > 0) {
          dispatch(setProjectEditorData({ ...projectEditorData, ...updates }));
        }
      } catch {
        // Parsing is best-effort — don't block the upload flow
      }
    }

    files.forEach((file, index) => {
      dispatch(
        addFileToUploadQueue({
          parentId: projectEditorData._id,
          parentType: AttachmentParentType.POST,
          category: IAttachmentCategory.postModel,
          resourceType: AttachmentType.model,
          fileName: generateControllerFileName(file.name, controller),
          fileHash: fileHashes[index].toString(),
          fileSize: file.size,
          preview: URL.createObjectURL(file),
          controller,
        })
      );
    });
  };

  const createFileChangeHandler = (controller: IControllerType) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      handleFileChange(event, controller);
    };
  };

  const handleRemoveFile = async (fileHash: string) => {
    setIsDeleting(fileHash);
    try {
      const uploadQueueFile = projectEditorData.fileUploadQueue.find(
        (file) =>
          file.fileHash === fileHash &&
          file.category === IAttachmentCategory.postModel
      );

      if (uploadQueueFile) {
        dispatch(
          removeFileFromUploadQueue({
            fileHash: uploadQueueFile.fileHash,
            fileName: uploadQueueFile.fileName,
            fileSize: uploadQueueFile.fileSize,
            category: IAttachmentCategory.postModel,
          })
        );
      }

      const existingModel = projectEditorData.models.find(
        (model) =>
          model.fileHash === fileHash &&
          model.category === IAttachmentCategory.postModel
      );
      if (!existingModel) return;
      await deleteAttachment({
        attachmentId: existingModel._id,
      });
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          models: projectEditorData.models.filter(
            (model) => model.fileHash !== fileHash
          ),
        })
      );
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setIsDeleting('');
    }
  };

  const handleControllerToggle = (controller: IControllerType) => {
    const isCurrentlySelected = getSelectedControllers().includes(controller);

    // If controller is being deselected, remove associated files
    if (isCurrentlySelected) {
      // Remove files from upload queue for this controller
      const updatedUploadQueue = projectEditorData.fileUploadQueue.filter(
        (file) =>
          !(
            file.category === IAttachmentCategory.postModel &&
            file.controller === controller
          )
      );

      // Remove existing models for this controller
      const modelsToKeep = projectEditorData.models.filter(
        (model) =>
          !(
            model.category === IAttachmentCategory.postModel &&
            model.controller === controller
          )
      );

      // Update both upload queue and models in a single dispatch
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          models: modelsToKeep,
          fileUploadQueue: updatedUploadQueue,
        })
      );
    }

    dispatch(toggleController(controller));
  };

  // Create selectors for each controller
  const redlineFiles = useSelector(
    selectFilesForController(IControllerType.REDLINE)
  );
  const massoFiles = useSelector(
    selectFilesForController(IControllerType.MASSO)
  );
  const buildboticsFiles = useSelector(
    selectFilesForController(IControllerType.BUILDOTICS)
  );
  const otherFiles = useSelector(
    selectFilesForController(IControllerType.OTHER)
  );

  const getFilesForController = (controller: IControllerType) => {
    switch (controller) {
      case IControllerType.REDLINE:
        return redlineFiles;
      case IControllerType.MASSO:
        return massoFiles;
      case IControllerType.BUILDOTICS:
        return buildboticsFiles;
      case IControllerType.OTHER:
        return otherFiles;
      default:
        return [];
    }
  };

  const isFileFromUploadQueue = (file: any): boolean => {
    return projectEditorData.fileUploadQueue.some(
      (uploadFile) => uploadFile.fileHash === file.fileHash
    );
  };

  const getFileSourceStatusIcon = (file: any) => {
    const isLocal = isFileFromUploadQueue(file);
    return isLocal ? (
      <HardDrive className="w-4 h-4 text-gray-500" />
    ) : (
      <Cloud className="w-4 h-4 text-gray-500" />
    );
  };

  const getSelectedCount = () => {
    return projectEditorData.selectedControllers.length;
  };

  // Auto-select controllers based on existing models
  useEffect(() => {
    if (controllersFromModels.length > 0) {
      // Only update if the selected controllers don't match the models
      const currentSelected = getSelectedControllers();
      const needsUpdate =
        !controllersFromModels.every((controller) =>
          currentSelected.includes(controller)
        ) || currentSelected.length !== controllersFromModels.length;

      if (needsUpdate) {
        // Add controllers that have models but aren't selected
        controllersFromModels.forEach((controller) => {
          if (!currentSelected.includes(controller)) {
            dispatch(toggleController(controller));
          }
        });
      }
    }
  }, [controllersFromModels, dispatch, getSelectedControllers]);

  // Set initial active tab when controllers are selected
  useEffect(() => {
    const selectedControllers = getSelectedControllers();
    const selectedControllersInOrder = getSelectedControllersInOrder();

    if (selectedControllers.length > 0) {
      // If no active tab or current active tab is not in selected controllers
      if (
        !activeTab ||
        !selectedControllers.includes(activeTab as IControllerType)
      ) {
        // Set active tab to the first controller in the consistent order
        const firstController = selectedControllersInOrder[0];
        if (firstController) {
          setActiveTab(firstController);
        }
      }
    } else {
      // If no controllers are selected, clear the active tab
      setActiveTab('');
    }
  }, [
    projectEditorData.selectedControllers,
    activeTab,
    getSelectedControllers,
    getSelectedControllersInOrder,
  ]);

  return (
    <div className="font-tertiary">
      <Card className="w-full max-w-6xl mx-auto">
        <CardContent className="p-4 sm:p-6">
          {/* Controller Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">
                Select Compatible Controllers
              </h3>
              {getSelectedCount() > 0 && (
                <Badge variant="secondary">{getSelectedCount()} selected</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 border rounded-lg p-3 sm:p-4 bg-gray-50">
              {CONTROLLER_ORDER.map((controller) => {
                const config = CONTROLLER_CONFIG[controller];
                const isSelected =
                  getSelectedControllers().includes(controller);

                return (
                  <div key={controller} className="flex items-center space-x-3">
                    <Checkbox
                      id={controller}
                      checked={isSelected}
                      onCheckedChange={() => handleControllerToggle(controller)}
                    />
                    <label
                      htmlFor={controller}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <div className="w-6 h-6 rounded-full flex items-center justify-center border">
                        {config.icon ? (
                          <config.icon className="w-4 h-4" />
                        ) : (
                          <span className="text-xs font-bold">
                            {config.initial}
                          </span>
                        )}
                      </div>
                      <span className="text-sm">{config.name}</span>
                    </label>
                  </div>
                );
              })}
            </div>

            {getSelectedCount() === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-2">
                <div className="text-yellow-600">⚠️</div>
                <p className="text-sm text-yellow-800">
                  Please select at least one controller to continue.
                </p>
              </div>
            )}
          </div>

          {/* Tabbed File Upload */}
          {getSelectedCount() > 0 && (
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full mt-4"
            >
              <TabsList className="flex w-full mb-0">
                {getSelectedControllersInOrder().map((controller) => {
                  const config = CONTROLLER_CONFIG[controller];
                  return (
                    <TabsTrigger
                      key={controller}
                      value={controller}
                      className="flex-1 flex flex-row items-center justify-center gap-1.5 py-2 min-w-0"
                    >
                      <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border">
                        {config.icon ? (
                          <config.icon className="w-3 h-3" />
                        ) : (
                          <span className="text-xs font-bold">
                            {config.initial}
                          </span>
                        )}
                      </div>
                      <span className="text-xs truncate min-w-0">
                        {config.name}
                      </span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {getSelectedControllersInOrder().map((controller) => {
                const config = CONTROLLER_CONFIG[controller];
                const controllerFiles = getFilesForController(controller);

                return (
                  <TabsContent
                    key={controller}
                    value={controller}
                    className="space-y-4 mt-3"
                  >
                    <div>
                      <h4 className="text-sm font-medium mb-2">
                        Upload Gcode files for {config.name}
                      </h4>
                      <DropZone onDrop={createFileChangeHandler(controller)}>
                        <Upload className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground" />
                        <div className="mt-4">
                          <p className="text-sm text-muted-foreground">
                            Select a file or drag and drop here
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Gcode
                          </p>
                        </div>
                        <Input
                          type="file"
                          className="hidden"
                          onChange={createFileChangeHandler(controller)}
                          accept={config.acceptString}
                          id={`file-upload-${controller}`}
                          multiple
                        />
                        <Button
                          variant="outline"
                          className="mt-4 border-gray-400 border-2"
                          onClick={() =>
                            document
                              .getElementById(`file-upload-${controller}`)
                              ?.click()
                          }
                        >
                          Select file
                        </Button>
                      </DropZone>
                    </div>

                    {/* File List for this controller */}
                    {controllerFiles.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">Uploaded Files</h3>
                        <div className="flex flex-col gap-2 max-h-64 overflow-auto">
                          <div className="space-y-2 max-w-4xl">
                            {controllerFiles.map((model) => (
                              <div
                                key={
                                  model.fileName
                                    ? `${model.fileName}-${model.fileSize}`
                                    : 'undefined-key'
                                }
                                className="flex items-center gap-2 py-2 px-3 border rounded-lg hover:bg-muted/50 transition-colors"
                              >
                                {/* File type icon */}
                                <div className="w-6 h-6 rounded-lg bg-gradient-to-b from-blue-600 to-blue-400 flex items-center justify-center flex-shrink-0">
                                  <FileBox className="w-3.5 h-3.5 text-white" />
                                </div>

                                {/* File info */}
                                <div className="min-w-0 flex-1 break-all text-sm font-medium line-clamp-2">
                                  {model.fileName || 'Unknown File'}
                                </div>

                                {/* Status icon */}
                                <div className="flex items-center gap-2">
                                  {getFileSourceStatusIcon(model)}

                                  {/* Delete button */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 rounded-full"
                                    onClick={() =>
                                      handleRemoveFile(model.fileHash)
                                    }
                                  >
                                    {isDeleting === model.fileHash ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <X className="h-3 w-3" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
          <ErrorMessage error={errors.models} />
        </CardContent>
      </Card>
    </div>
  );
}

export default ModelUpload;
